/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable class-methods-use-this */

import {
  ACCOUNT_CATEGORY,
  ACCOUNT_TYPE,
  BaseConfig,
  BaseController,
  BaseEmbedController,
  BaseEmbedControllerState,
  BillboardEvent,
  BROADCAST_CHANNELS,
  COMMUNICATION_NOTIFICATIONS,
  CommunicationWindowManager,
  ContactPayload,
  createLoggerMiddleware,
  createOriginMiddleware,
  DEFAULT_PREFERENCES,
  FEATURES_CONFIRM_WINDOW,
  FEATURES_DEFAULT_WALLET_WINDOW,
  FEATURES_PROVIDER_CHANGE_WINDOW,
  getPopupFeatures,
  ICommunicationProviderHandlers,
  InPageWalletProviderState,
  // PaymentParams,
  PopupHandler,
  PopupWithBcHandler,
  PROVIDER_NOTIFICATIONS,
  ProviderConfig,
  randomId,
  THEME,
  TopupInput,
  TransactionState,
  TransactionStatus,
  TX_EVENTS,
  TX_STATUS_UPDATE_EVENT_TYPE,
  UserInfo,
} from "@toruslabs/base-controllers";
import {
  createEngineStream,
  JRPCEngine,
  JRPCEngineEndCallback,
  JRPCEngineNextCallback,
  JRPCRequest,
  JRPCResponse,
  providerAsMiddleware,
  SafeEventEmitterProvider,
  setupMultiplex,
  Stream,
  Substream,
} from "@toruslabs/openlogin-jrpc";
import { LOGIN_PROVIDER_TYPE } from "@toruslabs/openlogin-utils";
import {
  AccountTrackerController,
  AvailableNetworkFees,
  BaseTransaction,
  ChainConfigController,
  ChainNetworkWithRequiredFields,
  CHAINS,
  CurrencyController,
  CustomFundgibleTokenDetails,
  DEFAULT_XRPL_CHAIN_CONFIGS,
  DEFAULT_XRPL_CHAIN_NETWORK_KEYS,
  ExtendedAddressPreferences,
  fundAccountFromXrplFaucet,
  getDefaultXRPLChainConfig,
  IProviderHandlers,
  IssuedCustomToken,
  ITopUpChannelEventData,
  KeyringController,
  LedgerState,
  LoadingState,
  Message,
  MESSAGE_EVENTS,
  MessageChannelEventData,
  MessageJrpcRequestInput,
  MessagesController,
  NetworkController,
  NftDetails,
  NftOffer,
  NftsTrackerController,
  PreferencesController,
  ProviderChangeChannelEventData,
  RPC_METHODS,
  SUPPORTED_SUBMIT_TRANSACTION_TYPES,
  TokensInfoController,
  TokensTrackerController,
  TransactionChannelEventData,
  TransactionController,
  TransactionJrpcRequestInput,
  TransactionJSONType,
  TransactionSerializer,
  TransactionTypes,
  UserCustomFungibleToken,
  XrplProviderConfig,
} from "@toruslabs/xrpl-controllers";
import BigNumber from "bignumber.js";
import { cloneDeep } from "lodash-es";
import log from "loglevel";
import pump from "pump";
import type { Duplex } from "readable-stream";

import OpenLoginWindowHandler from "@/auth/OpenLoginWindowHandler";
import configuration from "@/config";
import { BUTTON_POSITION, OpenLoginPopupResponse, TorusControllerConfig, TorusControllerState } from "@/utils/enums";
import { getUserLanguage, normalizeJson, setTheme, tagPublicKey } from "@/utils/helpers";
import topupPlugin from "@/utils/topup";

import { PKG } from "../const";

const defaultNetwork = ["development", "staging"].includes(process.env.NODE_ENV ?? "") ? CHAINS.XRPL_TESTNET : CHAINS.XRPL_MAINNET;

export const DEFAULT_CONFIG = {
  CurrencyControllerConfig: { api: configuration.api, pollInterval: 600_000 },
  NetworkControllerConfig: {
    providerConfig: getDefaultXRPLChainConfig(defaultNetwork),
  },
  PreferencesControllerConfig: {
    pollInterval: 180_000,
    api: configuration.api,
    signInPrefix: "Xrpl Signin",
    commonApiHost: configuration.commonApiHost,
  },
  TransactionControllerConfig: { txHistoryLimit: 40 },
  TokensInfoConfig: {
    api: configuration.api,
    interval: 600_000,
  },
  TokensTrackerConfig: {
    interval: 600_000,
  },
  NftTrackerConfig: { interval: 1800_000 },
  ChainConfigControllerConfig: {
    api: configuration.api,
    proxy: configuration.proxy,
  },
};

export const DEFAULT_STATE: TorusControllerState = {
  NftTrackerState: {
    nftPlaceholderPath: "/images/nft-placeholder.svg",
    nfts: {},
    nftOffers: {},
  },
  AccountTrackerState: { accounts: {} },
  KeyringControllerState: { wallets: [] },
  MessageControllerState: {
    unapprovedMsgs: {},
    unapprovedMsgCount: 0,
    messages: [],
  },
  CurrencyControllerState: {
    conversionDate: Date.now().toString(),
    conversionRate: 0,
    usdConversionRate: 0,
    currentCurrency: "usd",
    nativeCurrency: "xrp",
    ticker: "xrp",
  },
  NetworkControllerState: {
    chainId: getDefaultXRPLChainConfig(defaultNetwork).chainId,
    properties: {},
    providerConfig: getDefaultXRPLChainConfig(defaultNetwork),
  },
  PreferencesControllerState: {
    identities: {},
    selectedAddress: "",
  },
  TransactionControllerState: {
    transactions: {},
    unapprovedTxs: {},
    currentNetworkTxsList: [],
  },
  EmbedControllerState: {
    buttonPosition: BUTTON_POSITION.BOTTOM_RIGHT,
    isIFrameFullScreen: false,
    apiKey: "torus-default",
    oauthModalVisibility: false,
    loginInProgress: false,
    dappMetadata: {
      name: "",
      icon: "",
    },
  },
  TokensInfoState: {
    tokenInfoLoadingState: LoadingState.FULL_RELOAD,
  },
  TokensTrackerState: { lines: undefined, pendingLines: undefined },
  ChainConfigNetworksState: {
    chainCfgNetworkLoadingState: LoadingState.FULL_RELOAD,
    chainNetworks: DEFAULT_XRPL_CHAIN_CONFIGS,
    availableNetworks: DEFAULT_XRPL_CHAIN_NETWORK_KEYS,
  },
};

export default class TorusController extends BaseController<TorusControllerConfig, TorusControllerState> {
  public communicationManager = new CommunicationWindowManager();

  private networkController!: NetworkController;

  private tokensInfoController!: TokensInfoController;

  private chainCfgController!: ChainConfigController;

  private currencyController!: CurrencyController;

  private accountTracker!: AccountTrackerController;

  private keyringController!: KeyringController;

  private messageController!: MessagesController;

  private preferencesController!: PreferencesController;

  private nftController!: NftsTrackerController;

  private txController!: TransactionController;

  private tokensTrackerController!: TokensTrackerController;

  private embedController!: BaseEmbedController<BaseConfig, BaseEmbedControllerState>;

  private engine?: JRPCEngine;

  private communicationEngine?: JRPCEngine;

  //   private sendUpdate: DebouncedFunc<() => void>;

  constructor({ config, state }: { config: Partial<TorusControllerConfig>; state: Partial<TorusControllerState> }) {
    super({ config, state });
  }

  get origin(): string {
    return this.preferencesController.iframeOrigin;
  }

  get selectedPublicKey(): string {
    const address = this.selectedAddress;
    if (!address) return "";
    const pubKey = this.keyringController.getPublicKeyFromAddress(address);
    return tagPublicKey(pubKey);
  }

  get selectedAddress(): string {
    return this.preferencesController?.state.selectedAddress || "";
  }

  get selectedPrivateKey(): string {
    const address = this.selectedAddress;
    if (!address) return "";
    const wallet = this.keyringController.state.wallets.find((x) => x.address === address);
    return wallet?.privateKey || "";
  }

  get userXrpBalance(): string {
    const balance = this.accountTracker.state.accounts[this.selectedAddress]?.balance || "0x0";
    const value = new BigNumber(balance);
    return value.toString();
  }

  get locale(): string {
    return this.getAccountPreferences(this.selectedAddress)?.locale?.split("-")[0] || getUserLanguage();
  }

  get userInfo(): UserInfo {
    // Use only for UI
    return this.getAccountPreferences(this.selectedAddress)?.userInfo || cloneDeep(DEFAULT_PREFERENCES.userInfo);
  }

  get communicationProvider(): SafeEventEmitterProvider {
    return this.embedController._communicationProviderProxy;
  }

  get provider(): SafeEventEmitterProvider {
    return this.networkController._providerProxy as unknown as SafeEventEmitterProvider;
  }

  get publicKeys(): { publicKey: string; address: string }[] {
    return this.keyringController.state.wallets.map((x) => {
      return {
        publicKey: tagPublicKey(x.publicKey),
        address: x.address,
      };
    });
  }

  get currentCurrency(): string {
    return this.currencyController?.state?.currentCurrency;
  }

  /**
   * Always call init function before using this controller
   */
  public init({ config, state }: { config: Partial<TorusControllerConfig>; state: Partial<TorusControllerState> }): void {
    try {
      log.info(config, state, "restoring config & state");

      this.initialize();
      this.configure(config, true, true);
      this.update(state, true);
      this.networkController = new NetworkController({ config: this.config.NetworkControllerConfig, state: this.state.NetworkControllerState });
      this.initializeProvider();
      this.embedController = new BaseEmbedController({ config: {}, state: this.state.EmbedControllerState });
      this.initializeCommunicationProvider();

      this.currencyController = new CurrencyController({
        config: this.config.CurrencyControllerConfig,
        state: this.state.CurrencyControllerState,
      });
      this.currencyController.updateConversionRate();
      this.currencyController.scheduleConversionInterval();

      // initialize ChainConfigController
      this.chainCfgController = new ChainConfigController({
        config: this.config.ChainConfigControllerConfig,
        state: this.state.ChainConfigNetworksState,
      });

      // key management
      this.keyringController = new KeyringController({
        config: this.config.KeyringControllerConfig,
        state: this.state.KeyringControllerState,
      });

      this.preferencesController = new PreferencesController({
        state: this.state.PreferencesControllerState,
        config: this.config.PreferencesControllerConfig,
        provider: this.networkController._providerProxy,
        chainCfgController: this.chainCfgController,
        signAuthMessage: this.keyringController.signAuthMessage.bind(this.keyringController),
        getProviderConfig: this.networkController.getProviderConfig.bind(this.networkController),
        getNativeCurrency: this.currencyController.getNativeCurrency.bind(this.currencyController),
        getCurrentCurrency: this.currencyController.getCurrentCurrency.bind(this.currencyController),
        getConversionRate: this.currencyController.getConversionRate.bind(this.currencyController),
        getLastLedgerState: () => (this.networkController._ledgerTrackerProxy?.state._currentLedgerState ?? {}) as LedgerState,
      });

      this.tokensInfoController = new TokensInfoController({
        config: this.config.TokensInfoConfig,
        state: this.state.TokensInfoState,
        provider: this.networkController._providerProxy,
        getIdentities: () => this.preferencesController.state.identities,
        getProviderConfig: this.networkController.getProviderConfig.bind(this.networkController),
        getCurrentConversionRate: () => this.currencyController.state.usdConversionRate,
        getUsdConversionRate: this.currencyController.getConversionRate.bind(this.currencyController),
      });
      this.tokensInfoController.poll();

      this.tokensTrackerController = new TokensTrackerController({
        state: this.state.TokensTrackerState,
        config: this.config.TokensTrackerConfig,
        provider: this.networkController._providerProxy,
        getIdentities: () => this.preferencesController.state.identities,
        getProviderConfig: this.networkController.getProviderConfig.bind(this.networkController),
        getXRPLChainConfig: this.chainCfgController.getXrplChainConfig.bind(this.chainCfgController),
        getFormattedPastTransactions: (address: string) => this.preferencesController.getAddressState(address)?.formattedPastTransactions ?? [],
      });

      this.tokensTrackerController.poll();

      this.preferencesController
        .poll()
        .then(() => {
          return this.tokensTrackerController.refreshTokens();
        })
        .catch((err: unknown) => {
          log.error(err, "Error while polling preferences controller");
        });

      this.nftController = new NftsTrackerController({
        config: this.config.NftTrackerConfig,
        state: this.state.NftTrackerState,
        getIdentities: () => this.preferencesController.state.identities,
        getNfts: this.preferencesController.getNfts.bind(this.preferencesController),
        getNftOffers: this.preferencesController.getNftOffers.bind(this.preferencesController),
        getJwt: () => {
          const { selectedAddress } = this.preferencesController.state;
          return this.preferencesController.getAddressState(selectedAddress)?.jwtToken ?? "";
        },
      });

      this.nftController.poll();

      this.messageController = new MessagesController({ signMessage: this.keyringController.signMessage.bind(this.keyringController) });

      this.accountTracker = new AccountTrackerController({
        provider: this.networkController._providerProxy,
        state: this.state.AccountTrackerState,
        config: this.config.AccountTrackerConfig,
        ledgerTracker: this.networkController._ledgerTrackerProxy,
        getIdentities: () => this.preferencesController.state.identities,
        onPreferencesStateChange: (listener) => this.preferencesController.on("store", listener),
        getCurrentChainId: () => this.networkController.state.chainId,
      });
      // ensure accountTracker updates balances after network change
      this.networkController.on("networkDidChange", async () => {
        this.emit("networkDidChange");
        await this.accountTracker.refresh();
        await this.tokensInfoController.refreshTokensPrices(true);
        await this.tokensTrackerController.refreshTokens();

        // Get all completed nft offers that are accepted by others within the last 5 minutes
        const offers = await this.nftController.getRecentAcceptedNftOffersByOthers(this.selectedAddress);
        // Update nft cache if there are any accepted nft offers by others
        this.nftController.refreshNfts(offers.length ? this.selectedAddress : undefined);
        this.preferencesController.recalculatePastTx(this.state.PreferencesControllerState.selectedAddress as string);
        this.engine?.emit("notification", {
          method: PROVIDER_NOTIFICATIONS.CHAIN_CHANGED,
          params: {
            chainId: this.networkController.state.chainId,
          },
        });
      });

      this.txController = new TransactionController({
        config: this.config.TransactionControllerConfig,
        state: this.state.TransactionControllerState,
        accountTracker: this.accountTracker,
        ledgerTracker: this.networkController._ledgerTrackerProxy,
        provider: this.networkController._providerProxy,
        getCurrentChainId: this.networkController.getNetworkIdentifier.bind(this.networkController),
        signTransaction: this.keyringController.signTransaction.bind(this.keyringController),
      });

      this.networkController._ledgerTrackerProxy.on("latest", (ledgerState: LedgerState) => {
        this.accountTracker.configure({ _currentLedgerState: ledgerState });
        this.accountTracker.refresh();
        this.txController.sync(ledgerState);
      });

      this.chainCfgController.on("store", (state2) => {
        this.update({ ChainConfigNetworksState: state2 });
      });

      this.tokensInfoController.on("store", (state2) => {
        this.update({ TokensInfoState: state2 });
      });

      this.tokensTrackerController.on("store", async (state2) => {
        this.update({ TokensTrackerState: state2 });
      });

      this.txController.on(TX_EVENTS.TX_UNAPPROVED, (txMeta) => {
        this.emit(TX_EVENTS.TX_UNAPPROVED, txMeta);
      });

      this.txController.on(TX_EVENTS.TX_STATUS_UPDATE, (txMeta: TX_STATUS_UPDATE_EVENT_TYPE) => {
        const tx = this.txController.getTransaction(txMeta.txId);
        if (tx.transactionType === TransactionTypes.TrustSet && [TransactionStatus.submitted, TransactionStatus.confirmed].includes(txMeta.status)) {
          this.tokensTrackerController.refreshTokens();
        }
        if (
          ([TransactionTypes.NFTokenCreateOffer, TransactionTypes.NFTokenAcceptOffer, TransactionTypes.NFTokenCancelOffer] as string[]).includes(
            tx.transactionType
          ) &&
          [TransactionStatus.submitted, TransactionStatus.confirmed, TransactionStatus.failed].includes(txMeta.status)
        ) {
          const shouldUncache = tx.transactionType === TransactionTypes.NFTokenAcceptOffer && txMeta.status === TransactionStatus.confirmed;
          this.preferencesController
            .sync(this.selectedAddress)
            .then(() => this.nftController.refreshNfts(shouldUncache ? this.selectedAddress : undefined))
            .catch((err: unknown) => log.error(err));
        }
      });

      this.txController.on(TX_EVENTS.TX_CONFIRMED, (txMeta) => {
        this.emit(TX_EVENTS.TX_CONFIRMED, txMeta);
      });

      this.messageController.on(MESSAGE_EVENTS.UNAPPROVED_MSG, (msgData: Message) => {
        this.emit(MESSAGE_EVENTS.UNAPPROVED_MSG, msgData);
      });

      this.networkController.lookupNetwork();
      this.syncStore();

      // ensure isClientOpenAndUnlocked is updated when memState updates
      // this.subscribeEvent("update", (torusControllerState: unknown) => this._onStateUpdate(torusControllerState));

      // this.subscribe(this.sendUpdate.bind(this));

      // if (typeof options.rehydrate === "function") {
      //   setTimeout(() => {
      //     options.rehydrate();
      //   }, 50);
      // }
      // this.sendUpdate = debounce(this.privateSendUpdate.bind(this), 200);
    } catch (err) {
      log.error("[Error] TorusController::init::", (err as Error).stack);
    }
  }

  setOrigin(_: string): void {
    this.preferencesController.setIframeOrigin(origin);
  }

  public loginFromWidgetButton(): void {
    const id = randomId();
    this.toggleIframeFullScreen(id);
    this.once(id, () => {
      this.embedController.update({
        loginInProgress: true,
        oauthModalVisibility: true,
      });
    });
    this.once("LOGIN_RESPONSE", (error: string, address: string) => {
      const id2 = randomId();
      this.embedController.update({ oauthModalVisibility: false });
      setTimeout(() => {
        this.toggleIframeFullScreen(id2);
      }, 100);
      this.once(id2, () => {
        this.embedController.update({ loginInProgress: false });
      });
      if (error) {
        log.error(error);
      } else if (address) {
        this.engine?.emit("notification", {
          method: PROVIDER_NOTIFICATIONS.UNLOCK_STATE_CHANGED,
          params: {
            accounts: [address],
            isUnlocked: true,
          },
        });
        this.communicationEngine?.emit("notification", {
          method: COMMUNICATION_NOTIFICATIONS.USER_LOGGED_IN,
          params: {
            currentLoginProvider: this.getAccountPreferences(this.selectedAddress)?.userInfo.typeOfLogin || "",
          },
        });
      }
    });
  }

  public hideOAuthModal(): void {
    this.embedController.update({ oauthModalVisibility: false });
  }

  async changeProvider<T>(req: JRPCRequest<T>): Promise<boolean> {
    const params = req.params as unknown as XrplProviderConfig & { windowId: string };
    const channelName = `${BROADCAST_CHANNELS.PROVIDER_CHANGE_CHANNEL}_${params.windowId}`;
    const finalUrl = new URL(`${configuration.baseRoute}providerchange?&instanceId=${params.windowId}`);
    const providerChangeWindow = new PopupWithBcHandler<{ approve: boolean }, ProviderChangeChannelEventData>({
      state: {
        url: finalUrl,
        windowId: params.windowId,
      },
      config: {
        dappStorageKey: configuration.dappStorageKey || undefined,
        communicationEngine: this.communicationEngine as JRPCEngine,
        communicationWindowManager: this.communicationManager,
        target: "_blank",
        features: getPopupFeatures(FEATURES_PROVIDER_CHANGE_WINDOW),
      },
      instanceId: channelName,
    });
    const result = await providerChangeWindow.handleWithHandshake({
      origin: this.preferencesController.iframeOrigin,
      newNetwork: params,
      currentNetwork: this.networkController.state.providerConfig.networkKey,
      whitelabelData: {
        theme: this.getAccountPreferences(this.selectedAddress)?.theme || "light",
      },
    });
    const { approve = false } = result;
    if (approve) {
      this.networkController.setProviderConfig(req.params as unknown as XrplProviderConfig);
      return true;
    }
    throw new Error("user denied provider change request");
  }

  async handleTransactionPopup(txId: string, req: JRPCRequest<TransactionJSONType> & { origin: string; windowId: string }): Promise<void> {
    try {
      const { windowId } = req;
      const channelName = `${BROADCAST_CHANNELS.TRANSACTION_CHANNEL}_${windowId}`;
      const finalUrl = new URL(`${configuration.baseRoute}confirm?instanceId=${windowId}`);
      const normalizedTx = this.txController.getTransaction(txId);
      const popupPayload: TransactionChannelEventData = {
        rpcMethod: req.method,
        type: normalizedTx.transactionType,
        txParams: JSON.parse(JSON.stringify(normalizedTx.transaction)),
        origin: this.preferencesController.iframeOrigin,
        balance: new BigNumber(this.userXrpBalance).toString(),
        selectedCurrency: this.currencyController.state.currentCurrency,
        currencyRate: this.currencyController.state.conversionRate?.toString(),
        jwtToken: "",
        network: this.networkController.state.providerConfig.networkKey,
        whitelabelData: {
          theme: this.getAccountPreferences(this.selectedAddress)?.theme || "light",
        },
        networkDetails: { providerConfig: normalizeJson<XrplProviderConfig>(this.networkController.state.providerConfig) },
      };
      const txApproveWindow = new PopupWithBcHandler<{ approve: boolean }, TransactionChannelEventData>({
        state: {
          url: finalUrl,
          windowId,
        },
        config: {
          dappStorageKey: configuration.dappStorageKey || undefined,
          communicationEngine: this.communicationEngine,
          communicationWindowManager: this.communicationManager,
          target: "_blank",
          features: getPopupFeatures(FEATURES_CONFIRM_WINDOW),
        },
        instanceId: channelName,
      });
      // eslint-disable-next-line no-console
      console.log("popupPayload", popupPayload);
      const result = await txApproveWindow.handleWithHandshake(popupPayload);
      const { approve = false } = result;
      if (approve) {
        this.txController.approveTransaction(txId); // approve and publish
      } else {
        this.txController.setTxStatusRejected(txId);
      }
    } catch (error) {
      log.error(error);
      this.txController.setTxStatusRejected(txId);
    }
  }

  async embedhandleTopUp(req: JRPCRequest<TopupInput>): Promise<boolean> {
    try {
      const windowId = req.params?.windowId ?? "";
      const provider = req.params?.provider ?? "";
      const params = req.params?.params ?? {};
      const topUpChannelName = `${BROADCAST_CHANNELS.TOP_UP_CHANNEL}_${windowId}`;
      const confirmUrl = new URL(`${configuration.baseRoute}topUp?instanceId=${windowId}`);

      const popupPayload: ITopUpChannelEventData = {
        provider,
        selectedCurrency: params.selectedCurrency,
        cryptoAmount: params.cryptoAmount,
        selectedCryptoCurrency: params.selectedCryptoCurrency,
        whitelabelData: {
          theme: this.getAccountPreferences(this.selectedAddress)?.theme || "light",
        },
      };
      const topUpConfirmProceedPopUpWindow = new PopupWithBcHandler<{ approve: boolean }, ITopUpChannelEventData>({
        state: {
          url: confirmUrl,
          windowId,
        },
        config: {
          dappStorageKey: configuration.dappStorageKey || undefined,
          communicationEngine: this.communicationEngine,
          communicationWindowManager: this.communicationManager,
          target: "_blank",
          features: getPopupFeatures(FEATURES_CONFIRM_WINDOW),
        },
        instanceId: topUpChannelName,
      });
      const result = await topUpConfirmProceedPopUpWindow.handleWithHandshake(popupPayload);
      const { approve = false } = result;
      return approve;
    } catch (error) {
      log.error(error);
      return false;
    }
  }

  async handleSignMessagePopup(msgData: Message, req: JRPCRequest<Message> & { origin: string; windowId: string }): Promise<void> {
    try {
      const { windowId } = req;
      const channelName = `${BROADCAST_CHANNELS.MESSAGE_CHANNEL}_${windowId}`;
      const finalUrl = new URL(`${configuration.baseRoute}confirmMessage?instanceId=${windowId}`);
      const popupPayload: MessageChannelEventData = {
        msgParams: msgData,
        origin: this.preferencesController.iframeOrigin,
        whitelabelData: {
          theme: this.getAccountPreferences(this.selectedAddress)?.theme || "light",
        },
      };
      const msgApproveWindow = new PopupWithBcHandler<{ approve: boolean }, MessageChannelEventData>({
        state: {
          url: finalUrl,
          windowId,
        },
        config: {
          dappStorageKey: configuration.dappStorageKey || undefined,
          communicationEngine: this.communicationEngine,
          communicationWindowManager: this.communicationManager,
          target: "_blank",
          features: getPopupFeatures(FEATURES_CONFIRM_WINDOW),
        },
        instanceId: channelName,
      });
      const result = await msgApproveWindow.handleWithHandshake(popupPayload);
      const { approve = false } = result;
      if (approve) {
        this.messageController.signMessage(msgData.id);
      } else {
        this.messageController.rejectMsg(msgData.id);
      }
    } catch (error) {
      log.error(error);
      this.messageController.rejectMsg(msgData.id);
    }
  }

  setIFrameStatus(req: JRPCRequest<{ isIFrameFullScreen: boolean; rid?: string }>): void {
    const { isIFrameFullScreen = false, rid } = req.params || {};
    log.info("setting iframe status from embed", req.params);
    this.embedController.update({
      isIFrameFullScreen,
    });
    if (rid) this.emit(rid);
  }

  logout(req: JRPCRequest<[]>, res: JRPCResponse<boolean>, _: JRPCEngineNextCallback, end: JRPCEngineEndCallback): void {
    this.handleLogout();
    res.result = true;
    end();
  }

  public handleLogout(): void {
    this.emit("logout");
    this.engine?.emit("notification", {
      method: PROVIDER_NOTIFICATIONS.ACCOUNTS_CHANGED,
      params: [],
    });
    this.engine?.emit("notification", {
      method: PROVIDER_NOTIFICATIONS.UNLOCK_STATE_CHANGED,
      params: {
        accounts: [],
        isUnlocked: false,
      },
    });
    this.communicationEngine?.emit("notification", {
      method: COMMUNICATION_NOTIFICATIONS.USER_LOGGED_OUT,
    });
  }

  getPublicKeyFromAddress(address: string): string {
    const pubKey = this.keyringController.getPublicKeyFromAddress(address);
    return tagPublicKey(pubKey);
  }

  async newUnapprovedSubmitTxRequest(req: JRPCRequest<TransactionJrpcRequestInput> & { origin: string }): Promise<{ txHash: string }> {
    if (!req.params) throw new Error("params are required in request");
    const { transaction, txOptions = {} } = req.params;
    if (!transaction) {
      throw new Error("transaction is required in req.params");
    }
    if (!transaction.TransactionType) {
      throw new Error("TransactionType is required in transaction");
    }
    if (!SUPPORTED_SUBMIT_TRANSACTION_TYPES[transaction.TransactionType as string]) {
      throw new Error(
        `TransactionType ${transaction.TransactionType} is not supported with ${RPC_METHODS.SUBMIT_TRANSACTION}, Please use ${RPC_METHODS.SIGN_TRANSACTION} instead`
      );
    }

    const txRes = await this.txController.addTransaction(transaction, txOptions, req);
    const txHash = await txRes.result;
    return { txHash };
  }

  async newUnapprovedSignTxRequest(req: JRPCRequest<TransactionJrpcRequestInput> & { origin: string }): Promise<{ signedTxBlob: string }> {
    if (!req.params) throw new Error("params are required in request");
    const { transaction, txOptions = {} } = req.params;
    if (!transaction) {
      throw new Error("transaction is required in req.params");
    }

    if (!transaction.TransactionType) {
      throw new Error("TransactionType is required in transaction");
    }
    const txRes = await this.txController.addTransaction(transaction, txOptions, req);
    // wait for user to approve sign
    await txRes.result;
    const signedTxData = this.txController.getTransaction(txRes.transactionMeta.id);
    const txInstance = new TransactionSerializer(signedTxData.transaction);
    return { signedTxBlob: txInstance.SignedBlob };
  }

  async newUnsignedMessage(req: JRPCRequest<MessageJrpcRequestInput> & { origin: string }): Promise<{ signature: string }> {
    if (!req.params) throw new Error("params are required in request");
    const { message, from } = req.params;
    if (!message) {
      throw new Error("message is required in req.params");
    }
    if (!from) {
      throw new Error("from is required in req.params");
    }
    const msgRes = await this.messageController.addUnapprovedMessage(message, from, req);
    const signature = await msgRes.result;
    return { signature };
  }

  async approveTransaction(txId: string): Promise<void> {
    await this.txController.approveTransaction(txId);
  }

  getAccountNfts(address: string): NftDetails[] {
    return this.nftController.state.nfts[address];
  }

  getAccountNftOffers(address: string): NftOffer[] {
    return this.nftController.state.nftOffers[address];
  }

  getAccountPreferences(address: string): ExtendedAddressPreferences | undefined {
    return this.preferencesController?.getAddressState(address);
  }

  getProviderConfig(): ProviderConfig {
    return this.networkController.getProviderConfig();
  }

  async addAccount(privKey: string, userInfo: UserInfo, type: ACCOUNT_TYPE): Promise<string> {
    try {
      const walletsLen = this.keyringController.state.wallets.length;
      const address =
        type === ACCOUNT_CATEGORY.NORMAL ? this.keyringController.importAccount(privKey) : this.keyringController.importAccountFromSecret(privKey);
      const walletsLenPost = this.keyringController.state.wallets.length;
      if (walletsLenPost === walletsLen) {
        throw new Error("Duplicate Account, Account already exists");
      }
      const pubKey = this.keyringController.getPublicKeyFromAddress(address);
      const selectedAccountPreferences = this.selectedAddress ? this.getAccountPreferences(this.selectedAddress) : {};
      const locale = getUserLanguage();
      // here, user's preferences will be initiated as well as the `available xrpl chain networks list` is populated from server
      await this.preferencesController.initPreferences({ pubKey, address, calledFromEmbed: false, userInfo, locale, type });
      const addressState = this.preferencesController.getAddressState(address);
      setTheme(addressState?.theme === ((selectedAccountPreferences as ExtendedAddressPreferences)?.theme || "dark"));
      return address;
    } catch (err) {
      log.error("[Error] addAccount", err);
      throw new Error(`[Error] addAccount:: ${(err as Error).message}`);
    }
  }

  setSelectedAccount(address: string): void {
    this.preferencesController.setSelectedAddress(address);
    this.engine?.emit("notification", {
      method: PROVIDER_NOTIFICATIONS.ACCOUNTS_CHANGED,
      params: [address],
    });
  }

  async setCrashReport(status: boolean): Promise<boolean> {
    return this.preferencesController.setCrashReport(status);
  }

  async addContact(contactPayload: ContactPayload): Promise<boolean> {
    return this.preferencesController.addContact(contactPayload);
  }

  async deleteContact(contactId: number): Promise<boolean> {
    return this.preferencesController.deleteContact(contactId);
  }

  async setTheme(theme: THEME): Promise<boolean> {
    return this.preferencesController.setUserTheme(theme);
  }

  async setDefaultCurrency(currency: string): Promise<boolean> {
    const { ticker } = this.networkController.getProviderConfig();
    // This is XRP
    this.currencyController.setNativeCurrency(ticker);
    // This is USD
    this.currencyController.setCurrentCurrency(currency);
    await this.currencyController.updateConversionRate();
    await this.tokensInfoController.refreshTokensPrices();
    return this.preferencesController.setSelectedCurrency({ selectedCurrency: currency });
  }

  async setLocale(locale: string): Promise<boolean> {
    return this.preferencesController.setUserLocale(locale);
  }

  async getBillboardData(): Promise<BillboardEvent[]> {
    return this.preferencesController.getBillBoardData();
  }

  setNetwork(providerConfig: XrplProviderConfig): void {
    this.networkController.setProviderConfig(providerConfig);
  }

  async getAvailableNetworkFee(): Promise<AvailableNetworkFees> {
    return this.networkController._ledgerTrackerProxy.getAvailableNetworkFee();
  }

  async refreshChainConfigNetworks() {
    const selectedAddrState = this.preferencesController.getAddressState(this.selectedAddress);
    if (selectedAddrState?.jwtToken) {
      await this.chainCfgController.refreshChainNetworks({ jwt: selectedAddrState.jwtToken });
    }
  }

  getTransactionInstanceFromJson(txJson: TransactionJSONType): BaseTransaction {
    return this.txController.getTransactionInstanceFromJson(txJson);
  }

  toggleIframeFullScreen(id?: string): void {
    const newState = !this.embedController?.state.isIFrameFullScreen;
    log.info("toggling iframe status to", newState);
    this.communicationEngine?.emit("notification", {
      method: COMMUNICATION_NOTIFICATIONS.IFRAME_STATUS,
      params: {
        isFullScreen: newState,
        rid: id,
      },
    });
  }

  showWalletPopup(path: string, instanceId: string, action?: string): void {
    const url = `${configuration.baseRoute}${path}?instanceId=${instanceId}${action ? `&action=${action}` : ""}`;
    const finalUrl = new URL(url);
    const walletPopupWindow = new PopupHandler({
      config: {
        features: getPopupFeatures(FEATURES_DEFAULT_WALLET_WINDOW),
      },
      state: { url: finalUrl },
    });
    walletPopupWindow.open();
  }

  /**
   * Used to create a multiplexed stream for connecting to an untrusted context
   * like a Dapp or other extension.
   */
  setupUnTrustedCommunication(connectionStream: Duplex, originDomain: string): void {
    // connect features && for test cases
    const torusMux = setupMultiplex(connectionStream);
    // We create the mux so that we can handle phishing stream here
    const providerStream = torusMux.getStream("provider");
    this.setupProviderConnection(providerStream as Substream, originDomain);
  }

  /**
   * A method for serving our ethereum provider over a given stream.
   */
  setupProviderConnection(providerStream: Substream, sender: string): void {
    // break violently
    const senderUrl = new URL(sender);

    const engine = this.setupProviderEngine({ origin: senderUrl.origin });
    log.info("initializing preferences controller with hostname", sender, senderUrl.origin);

    this.preferencesController.setIframeOrigin(senderUrl.origin);
    this.engine = engine;

    // setup connection
    const engineStream = createEngineStream({ engine });

    pump(providerStream as unknown as Stream, engineStream as unknown as Stream, providerStream as unknown as Stream, (error: Error | undefined) => {
      if (error) {
        // cleanup filter polyfill middleware
        this.engine = undefined;
        log.error(error);
      }
    });
  }

  /**
   * A method for creating a provider that is safely restricted for the requesting domain.
   * */
  setupProviderEngine({ origin }: { origin: string }): JRPCEngine {
    // setup json rpc engine stack
    const engine = new JRPCEngine();
    const { _providerProxy: providerProxy } = this.networkController;

    // create subscription polyfill middleware
    // const subscriptionManager = createSubscriptionManager({ provider, blockTracker });
    // subscriptionManager.events.on("notification", (message) => engine.emit("notification", message));

    // metadata
    engine.push(createOriginMiddleware({ origin }));
    engine.push(createLoggerMiddleware({ origin }));

    // TODO
    // engine.push(
    //   createMethodMiddleware({
    //     origin,
    //     getProviderState: this.getProviderState.bind(this),
    //     getCurrentChainId: this.networkController.getCurrentChainId.bind(this.networkController),
    //   })
    // );

    // filter and subscription polyfills
    // engine.push(subscriptionManager.middleware);
    // forward to metamask primary provider
    engine.push(providerAsMiddleware(providerProxy));
    return engine;
  }

  setupCommunicationChannel(connectionStream: Duplex, originDomain: string): void {
    // connect features && for test cases
    const torusMux = setupMultiplex(connectionStream);
    // We create the mux so that we can handle phishing stream here
    const providerStream = torusMux.getStream("provider");
    this.setupCommunicationProviderConnection(providerStream as Substream, originDomain);
  }

  /**
   * A method for serving our ethereum provider over a given stream.
   */
  setupCommunicationProviderConnection(providerStream: Substream, sender: string): void {
    // break violently
    const senderUrl = new URL(sender);

    const engine = this.setupCommunicationProviderEngine({ origin: senderUrl.origin });
    this.communicationEngine = engine;
    // setup connection
    const engineStream = createEngineStream({ engine });

    pump(providerStream as unknown as Stream, engineStream as unknown as Stream, providerStream as unknown as Stream, (error: Error | undefined) => {
      if (error) {
        // cleanup filter polyfill middleware
        this.communicationEngine = undefined;
        log.error(error);
      }
    });
  }

  /**
   * A method for creating a provider that is safely restricted for the requesting domain.
   * */
  setupCommunicationProviderEngine({ origin }: { origin: string }): JRPCEngine {
    const { _communicationProviderProxy: communicationProviderProxy } = this.embedController;
    // setup json rpc engine stack
    const engine = new JRPCEngine();
    // const { _providerProxy } = this.networkController;

    // create subscription polyfill middleware
    // const subscriptionManager = createSubscriptionManager({ provider, blockTracker });
    // subscriptionManager.events.on("notification", (message) => engine.emit("notification", message));

    // metadata
    engine.push(createOriginMiddleware({ origin }));
    engine.push(createLoggerMiddleware({ origin }));
    engine.push(providerAsMiddleware(communicationProviderProxy));

    // TODO
    // engine.push(
    //   createMethodMiddleware({
    //     origin,
    //     getProviderState: this.getProviderState.bind(this),
    //     getCurrentChainId: this.networkController.getCurrentChainId.bind(this.networkController),
    //   })
    // );

    // filter and subscription polyfills
    // engine.push(subscriptionManager.middleware);
    // forward to metamask primary provider
    // engine.push(providerAsMiddleware(_providerProxy));
    return engine;
  }

  async handleTopUp(provider: string, params: any = {}): Promise<void> {
    try {
      const finalParams = params;
      finalParams.jwtToken = this.getAccountPreferences(this.selectedAddress)?.jwtToken || "";
      finalParams.address = this.selectedAddress;
      const engine = {
        communicationEngine: this.communicationEngine,
        communicationWindowManager: this.communicationManager,
      };
      await topupPlugin[provider].getOrder(finalParams, engine);
    } catch (err) {
      log.error(err);
      throw err;
    }
  }

  public async triggerLogin({
    loginProvider,
    login_hint: loginHint,
  }: {
    loginProvider: LOGIN_PROVIDER_TYPE;
    login_hint?: string;
  }): Promise<OpenLoginPopupResponse> {
    try {
      const handler = new OpenLoginWindowHandler({
        loginProvider,
        extraLoginOptions: loginHint ? { login_hint: loginHint } : {},
      });
      const result = await handler.handleLoginWindow({
        communicationEngine: this.communicationEngine,
        communicationWindowManager: this.communicationManager,
      });

      if (!result) {
        throw new Error("TorusController::handleLoginWindow::error");
      }
      const { keys, userInfo } = result;
      const updatedUserInfo = { ...userInfo, name: userInfo.name ?? "", email: userInfo.email ?? "", profileImage: userInfo.profileImage ?? "" };
      const address = await this.addAccount(keys[0].privKey.padStart(64, "0"), updatedUserInfo, ACCOUNT_CATEGORY.NORMAL);
      this.setSelectedAccount(address);
      this.emit("LOGIN_RESPONSE", null, address);
      await this.tokensInfoController.refreshTokensPrices();
      await this.tokensTrackerController.refreshTokens();
      await this.nftController.refreshNfts();
      return result;
    } catch (error) {
      this.emit("LOGIN_RESPONSE", (error as Error)?.message);
      log.error(error);
      throw error;
    }
  }

  checkIsAccountActivated(address: string): Promise<boolean> {
    return this.accountTracker.checkIsAccountActivated(address, this.networkController._ledgerTrackerProxy?.state._currentLedgerState);
  }

  async fundAccountFromFaucet(address: string, network: string) {
    // fund account if it's not activated and not enough balance, in TESTNET
    const fundResp = await fundAccountFromXrplFaucet(address, network);
  }

  getXrplChainConfig(chainId: string) {
    return this.chainCfgController.getXrplChainConfig(chainId);
  }

  // create new user-defined custom xrp network
  async createCustomXrpNetwork(xrplChainConfig: Omit<ChainNetworkWithRequiredFields, "chainId">) {
    await this.preferencesController.createXrpChainNetwork(xrplChainConfig);
  }

  // update existing user-defined xrp network
  async updateCustomXrpNetwork(xrpNetwork: ChainNetworkWithRequiredFields) {
    await this.preferencesController.updateXrpChainNetwork(xrpNetwork);
  }

  // save custom fungible token details in backend
  async saveCustomFungibleTokenDetails(tokenDetails: UserCustomFungibleToken) {
    await this.tokensInfoController.saveCustomFungibleTokenDetails({ tokenDetails });
  }

  async removeCustomFungibleToken(tokenDetails: UserCustomFungibleToken) {
    await this.tokensInfoController.deleteCustomFungibleTokenDetails({ tokenDetails });
  }

  // fetch custom fungible token details
  async fetchCustomFungibleTokenDetails(fetchParams: {
    currencyCode: string;
    issuerAddress: string;
  }): Promise<CustomFundgibleTokenDetails | IssuedCustomToken> {
    const tokenDetails = await this.tokensInfoController.fetchCustomFungibleTokenDetails(fetchParams);
    return tokenDetails;
  }

  async fetchCustomFungibleTokenDetailsByClientCode(clientCode: string): Promise<IssuedCustomToken> {
    const token = await this.tokensInfoController.fetchCustomFungibleTokenDetailsByClientCode(clientCode);
    return token;
  }

  // delete existing user-defined xrp network
  async deleteCustomXrpNetwork(chainId: string) {
    await this.preferencesController.deleteXrpChainNetwork(chainId);
  }

  private async topup(req: JRPCRequest<TopupInput>) {
    return this.embedhandleTopUp(req);
  }

  private syncStore() {
    // Listen to controller changes
    this.preferencesController.on("store", (state) => {
      this.update({ PreferencesControllerState: state });
    });

    this.currencyController.on("store", (state) => {
      this.update({ CurrencyControllerState: state });
    });

    this.networkController.on("store", (state) => {
      this.update({ NetworkControllerState: state });
    });

    this.accountTracker.on("store", (state) => {
      this.update({ AccountTrackerState: state });
    });

    this.keyringController.on("store", (state) => {
      this.update({ KeyringControllerState: state });
    });

    this.txController.on("store", (state: TransactionState<TransactionJSONType>) => {
      this.update({ TransactionControllerState: state });
      Object.keys(state.transactions).forEach(async (txId) => {
        const txn = state.transactions[txId];

        let nftInfo: NftDetails | NftOffer | NftOffer[] | undefined;

        if (txn.transaction.TransactionType === TransactionTypes.NFTokenCreateOffer && txn.transaction.NFTokenID) {
          nftInfo = await this.nftController.getNftById(this.preferencesController.state.selectedAddress, txn.transaction.NFTokenID as string);
        }
        if (txn.transaction.TransactionType === TransactionTypes.NFTokenAcceptOffer && txn.transaction.NFTokenSellOffer) {
          nftInfo = await this.nftController.getNftOfferById(
            this.preferencesController.state.selectedAddress,
            txn.transaction.NFTokenSellOffer as string
          );
        }
        if (txn.transaction.TransactionType === TransactionTypes.NFTokenCancelOffer && txn.transaction.NFTokenOffers) {
          nftInfo = await this.nftController.getNftOfferById(
            this.preferencesController.state.selectedAddress,
            txn.transaction.NFTokenOffers as string[]
          );
        }

        const patchNewTx = (info?: Exclude<typeof nftInfo, unknown[]>): Promise<void> =>
          this.preferencesController
            .patchNewTx(state.transactions[txId], this.preferencesController.state.selectedAddress, info)
            .catch((err: unknown) => {
              log.error("error while patching a new tx", err);
            });

        if (nftInfo) {
          nftInfo = (Array.isArray(nftInfo) ? nftInfo : [nftInfo]) as NftOffer[];
          nftInfo.filter(Boolean).map((info: NftOffer) => patchNewTx(info));
        } else {
          patchNewTx();
        }
      });
    });

    this.embedController.on("store", (state) => {
      this.update({ EmbedControllerState: state });
    });
  }

  private initializeProvider(): SafeEventEmitterProvider {
    const providerHandlers: IProviderHandlers = {
      version: PKG.version,
      // account management
      getAccounts: async (req: JRPCRequest<unknown>) => {
        const accounts = await this.requestAccounts(req);
        this.engine?.emit("notification", {
          method: PROVIDER_NOTIFICATIONS.UNLOCK_STATE_CHANGED,
          params: {
            accounts,
            isUnlocked: accounts.length > 0,
          },
        });
        this.communicationEngine?.emit("notification", {
          method: COMMUNICATION_NOTIFICATIONS.USER_LOGGED_IN,
          params: { currentLoginProvider: this.getAccountPreferences(this.selectedAddress)?.userInfo.typeOfLogin || "" },
        });
        return accounts;
      },
      // tx signing
      submitTransaction: this.newUnapprovedSubmitTxRequest.bind(this),
      signTransaction: this.newUnapprovedSignTxRequest.bind(this),
      signMessage: this.newUnsignedMessage.bind(this),
      getProviderState(
        req: JRPCRequest<[]>,
        res: JRPCResponse<InPageWalletProviderState>,
        next: JRPCEngineNextCallback,
        end: JRPCEngineEndCallback
      ): void {
        throw new Error("Function not implemented.");
      },
    };
    const providerProxy = this.networkController.initializeProvider(providerHandlers);
    return providerProxy;
  }

  private async requestAccounts(req: JRPCRequest<unknown>): Promise<string[]> {
    return new Promise((resolve, reject) => {
      const [requestedLoginProvider, loginHint] = req.params as string[];
      const currentLoginProvider = this.getAccountPreferences(this.selectedAddress)?.userInfo.typeOfLogin;
      if (requestedLoginProvider) {
        if (requestedLoginProvider === currentLoginProvider && this.selectedAddress) {
          resolve([this.selectedAddress]);
        } else {
          // To login with the requested provider
          // On Embed, we have a window waiting... we need to tell it to login
          this.embedController.update({ loginInProgress: true, oauthModalVisibility: false });
          this.triggerLogin({ loginProvider: requestedLoginProvider as LOGIN_PROVIDER_TYPE, login_hint: loginHint });
          this.once("LOGIN_RESPONSE", (error: string, address: string) => {
            if (error) reject(new Error(error));
            else resolve([address]);
            this.embedController.update({ loginInProgress: false, oauthModalVisibility: false });
          });
        }
      } else if (this.selectedAddress) resolve([this.selectedAddress]);
      else {
        // We show the modal to login
        this.embedController.update({ loginInProgress: true, oauthModalVisibility: true });
        this.once("LOGIN_RESPONSE", (error: string, address: string) => {
          if (error) reject(new Error(error));
          else resolve([address]);
          this.embedController.update({ loginInProgress: false, oauthModalVisibility: false });
        });
      }
    });
  }

  private initializeCommunicationProvider() {
    const commProviderHandlers: ICommunicationProviderHandlers = {
      loginWithPrivateKey: () => {
        throw new Error("Unsupported method");
      },
      setIFrameStatus: this.setIFrameStatus.bind(this),
      changeProvider: this.changeProvider.bind(this),
      logout: this.logout.bind(this),
      getUserInfo: (req, res, _, end) => {
        res.result = normalizeJson<UserInfo>(this.userInfo);
        end();
      },
      getWalletInstanceId: () => {
        return "";
      },
      topup: this.topup.bind(this),
      handleWindowRpc: this.communicationManager.handleWindowRpc,
      getProviderState: (req, res, _, end) => {
        res.result = {
          currentLoginProvider: this.getAccountPreferences(this.selectedAddress)?.userInfo.typeOfLogin || "",
          isLoggedIn: !!this.selectedPublicKey,
        };
        end();
      },
      // // TODO: Properly implement the following methods!!!
      showCheckout: (req, res, _, end) => {
        throw new Error("Not implemented!");
      },
      showWalletConnect: (req, res, _, end) => {
        throw new Error("Not implemented!");
      },
      showWalletUi: (req, res, _, end) => {
        throw new Error("Not implemented!");
      },
      showWindowBlockAlert: (req, res, _, end) => {
        throw new Error("Not implemented!");
      },
    };
    this.embedController.initializeProvider(commProviderHandlers);
  }
}
